import React, { useState } from 'react';
import Modal from '../common/Modal';
import { Appointment, AppointmentStatus } from '../../types/appointment.types';
import { barberDashboardService, PaymentRequest } from '../../services/barberDashboardService';
import { appointmentService } from '../../services/appointmentService';
import { useToast } from '../../context/ToastContext';
import { getLocalDateString } from '../../utils/dateUtils';

interface AppointmentActionModalProps {
    appointment: Appointment | null;
    isOpen: boolean;
    onClose: () => void;
    onAppointmentUpdated: () => void;
    pricePerService: number;
}

type ActionView = 'main' | 'complete' | 'cancel' | 'reschedule' | 'delegate';

const AppointmentActionModal: React.FC<AppointmentActionModalProps> = ({
    appointment,
    isOpen,
    onClose,
    onAppointmentUpdated,
    pricePerService
}) => {
    const { showToast } = useToast();
    const [currentView, setCurrentView] = useState<ActionView>('main');
    const [submitting, setSubmitting] = useState(false);
    const [availableBarbers, setAvailableBarbers] = useState<{id: number, name: string, nickname: string}[]>([]);
    const [selectedBarberId, setSelectedBarberId] = useState<number | ''>('');
    const [loadingBarbers, setLoadingBarbers] = useState(false);

    // Payment form state
    const [paymentForm, setPaymentForm] = useState<PaymentRequest>({
        appointmentId: 0,
        amount: pricePerService,
        paymentMethod: 'CASH',
        transactionReference: ''
    });
    const [receiptImage, setReceiptImage] = useState<File | null>(null);
    const [receiptPreview, setReceiptPreview] = useState<string | null>(null);

    // Cancel form state
    const [cancelReason, setCancelReason] = useState('');

    // Reschedule form state
    const [rescheduleDate, setRescheduleDate] = useState('');
    const [rescheduleTime, setRescheduleTime] = useState('');
    const [rescheduleReason, setRescheduleReason] = useState('');

    const resetState = () => {
        setCurrentView('main');
        setPaymentForm({
            appointmentId: 0,
            amount: pricePerService,
            paymentMethod: 'CASH',
            transactionReference: ''
        });
        setReceiptImage(null);
        setReceiptPreview(null);
        setCancelReason('');
        setRescheduleDate('');
        setRescheduleTime('');
        setRescheduleReason('');
        setAvailableBarbers([]);
        setSelectedBarberId('');
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const handleImageCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setReceiptImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setReceiptPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const requiresReceipt = (method: string) => {
        return method === 'BANK_TRANSFER' || method === 'MERCADO_PAGO';
    };

    // Start the appointment (mark as IN_PROGRESS)
    const handleStartAppointment = async () => {
        if (!appointment) return;
        setSubmitting(true);
        try {
            await barberDashboardService.markAsInProgress(appointment.id);
            showToast('Turno iniciado', 'success');
            onAppointmentUpdated();
            handleClose();
        } catch (error) {
            console.error('Error starting appointment:', error);
            showToast('Error al iniciar el turno', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Complete with payment or update existing payment
    const handleCompleteWithPayment = async () => {
        if (!appointment) return;

        if (requiresReceipt(paymentForm.paymentMethod) && !receiptImage && !appointment.receiptImageUrl) {
            showToast('Debe subir una foto del comprobante', 'warning');
            return;
        }

        setSubmitting(true);
        try {
            let updatedForm = {
                ...paymentForm,
                appointmentId: appointment.id
            };

            if (receiptImage) {
                const uploadResponse = await barberDashboardService.uploadReceiptImage(receiptImage);
                updatedForm.receiptImageUrl = uploadResponse.url;
            } else if (appointment.receiptImageUrl) {
                updatedForm.receiptImageUrl = appointment.receiptImageUrl;
            }

            if (appointment.status === 'COMPLETED' && appointment.paymentId) {
                await barberDashboardService.updatePayment(appointment.paymentId, updatedForm);
                showToast('Pago actualizado correctamente', 'success');
            } else {
                await barberDashboardService.registerPayment(updatedForm);
                showToast('Pago registrado y turno completado', 'success');
            }
            
            onAppointmentUpdated();
            handleClose();
        } catch (error) {
            console.error('Error with payment:', error);
            showToast('Error al procesar el pago', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Complete without payment (pending)
    const handleCompletePending = async () => {
        if (!appointment) return;
        setSubmitting(true);
        try {
            await barberDashboardService.completeWithPendingPayment(appointment.id);
            showToast('Turno completado. Pago quedó pendiente.', 'success');
            onAppointmentUpdated();
            handleClose();
        } catch (error) {
            console.error('Error completing appointment:', error);
            showToast('Error al completar el turno', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Cancel appointment
    const handleCancelAppointment = async () => {
        if (!appointment) return;
        if (!cancelReason.trim()) {
            showToast('Debe ingresar un motivo de cancelación', 'warning');
            return;
        }

        setSubmitting(true);
        try {
            await barberDashboardService.cancelAppointment(appointment.id, cancelReason);
            showToast('Turno cancelado. Se notificará al cliente.', 'success');
            onAppointmentUpdated();
            handleClose();
        } catch (error) {
            console.error('Error cancelling appointment:', error);
            showToast('Error al cancelar el turno', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Reschedule appointment
    const handleRescheduleAppointment = async () => {
        if (!appointment) return;
        if (!rescheduleDate || !rescheduleTime) {
            showToast('Debe seleccionar fecha y hora', 'warning');
            return;
        }
        if (!rescheduleReason.trim()) {
            showToast('Debe ingresar un motivo de reprogramación', 'warning');
            return;
        }

        setSubmitting(true);
        try {
            await barberDashboardService.rescheduleAppointment(appointment.id, rescheduleDate, rescheduleTime, rescheduleReason);
            showToast('Turno reprogramado. Se notificará al cliente por email.', 'success');
            onAppointmentUpdated();
            handleClose();
        } catch (error) {
            console.error('Error rescheduling appointment:', error);
            showToast('Error al reprogramar el turno', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Load available barbers for delegation
    const loadAvailableBarbers = async () => {
        if (!appointment) return;
        setLoadingBarbers(true);
        try {
            const barbers = await appointmentService.getAvailableBarbersForDelegation(appointment.id);
            setAvailableBarbers(barbers);
        } catch (error) {
            console.error('Error loading available barbers:', error);
            showToast('Error al cargar compañeros disponibles', 'error');
        } finally {
            setLoadingBarbers(false);
        }
    };

    // Delegate appointment
    const handleDelegateAppointment = async () => {
        if (!appointment) return;
        if (!selectedBarberId) {
            showToast('Debe seleccionar un compañero', 'warning');
            return;
        }

        setSubmitting(true);
        try {
            await appointmentService.delegateAppointment(appointment.id, Number(selectedBarberId));
            showToast('Turno delegado exitosamente', 'success');
            onAppointmentUpdated();
            handleClose();
        } catch (error: any) {
            console.error('Error delegating appointment:', error);
            showToast(error.response?.data?.message || 'Error al delegar el turno', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusBadge = (status: AppointmentStatus) => {
        const statusConfig: Record<AppointmentStatus, { bg: string; text: string; label: string }> = {
            PENDING: { bg: '#f59e0b20', text: '#f59e0b', label: 'Pendiente' },
            CONFIRMED: { bg: '#3b82f620', text: '#60a5fa', label: 'Confirmado' },
            IN_PROGRESS: { bg: '#8b5cf620', text: '#a78bfa', label: 'En Progreso' },
            COMPLETED: { bg: '#22c55e20', text: '#22c55e', label: 'Completado' },
            CANCELLED: { bg: '#ef444420', text: '#ef4444', label: 'Cancelado' },
            NO_SHOW: { bg: '#52525220', text: '#737373', label: 'No Asistió' }
        };
        const config = statusConfig[status];
        return (
            <span style={{
                backgroundColor: config.bg,
                color: config.text,
                padding: '0.25rem 0.75rem',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: '600'
            }}>
                {config.label}
            </span>
        );
    };

    if (!appointment) return null;

    const canStart = appointment.status === 'CONFIRMED' || appointment.status === 'PENDING';
    const canComplete = appointment.status === 'IN_PROGRESS';
    const canCancel = appointment.status !== 'COMPLETED' && appointment.status !== 'CANCELLED';
    const canReschedule = appointment.status !== 'COMPLETED' && appointment.status !== 'CANCELLED' && appointment.status !== 'IN_PROGRESS';
    const canDelegate = appointment.bundleId != null && (appointment.status === 'CONFIRMED' || appointment.status === 'IN_PROGRESS');
    const canEditPayment = appointment.status === 'COMPLETED' && appointment.paymentId != null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={currentView === 'main' ? 'Gestionar Turno' :
                currentView === 'complete' ? 'Finalizar y Cobrar' :
                currentView === 'delegate' ? 'Delegar a Compañero' :
                    currentView === 'cancel' ? 'Cancelar Turno' : 'Reprogramar Turno'}
        >
            {/* Main View - Appointment Info & Actions */}
            {currentView === 'main' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {/* Appointment Info */}
                    <div style={{
                        backgroundColor: '#1a1a1a',
                        padding: '1rem',
                        borderRadius: '0.75rem',
                        border: '1px solid #2a2a2a'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                <h3 style={{ margin: 0, fontWeight: '700', fontSize: '1.125rem', color: '#e5e5e5' }}>
                                    {appointment.clientName}
                                </h3>
                                {appointment.clientPhone && (
                                    <a 
                                        href={`https://wa.me/${appointment.clientPhone.replace(/\\D/g, '')}`} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        style={{ 
                                            display: 'inline-flex', 
                                            alignItems: 'center', 
                                            gap: '0.35rem', 
                                            color: '#25D366', 
                                            textDecoration: 'none', 
                                            fontSize: '0.85rem',
                                            fontWeight: '600',
                                            backgroundColor: '#25D36620',
                                            padding: '0.35rem 0.75rem',
                                            borderRadius: '0.5rem',
                                            width: 'fit-content',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#25D36630';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#25D36620';
                                        }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                            <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232"/>
                                        </svg>
                                        Contactar ({appointment.clientPhone})
                                    </a>
                                )}
                            </div>
                            {getStatusBadge(appointment.status)}
                        </div>
                        <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem', color: '#a3a3a3' }}>
                            <p style={{ margin: 0 }}>📅 {new Date(appointment.appointmentDate).toLocaleDateString('es-AR', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' })}</p>
                            <p style={{ margin: 0 }}>🕐 {appointment.startTime.substring(0, 5)} - {appointment.endTime.substring(0, 5)}</p>
                            <p style={{ margin: 0 }}>✂️ {appointment.serviceDescription || 'Corte de Pelo'}</p>
                            <p style={{ margin: 0 }}>📍 {appointment.branchName}</p>
                            {appointment.receiptImageUrl && (
                                <p style={{ margin: 0 }}>
                                    🧾 <a href={appointment.receiptImageUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', textDecoration: 'underline' }}>Ver Comprobante Subido</a>
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {/* Start Button */}
                        {canStart && (
                            <button
                                className="btn"
                                onClick={handleStartAppointment}
                                disabled={submitting}
                                style={{
                                    backgroundColor: '#8b5cf6',
                                    color: 'white',
                                    padding: '1rem',
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem'
                                }}
                            >
                                ▶️ Iniciar Turno
                            </button>
                        )}

                        {/* Complete Button */}
                        {canComplete && (
                            <button
                                className="btn"
                                onClick={() => {
                                    setPaymentForm(prev => ({
                                        ...prev,
                                        appointmentId: appointment.id,
                                        amount: appointment.servicePrice || pricePerService
                                    }));
                                    setCurrentView('complete');
                                }}
                                disabled={submitting}
                                style={{
                                    backgroundColor: '#10b981',
                                    color: 'white',
                                    padding: '1rem',
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem'
                                }}
                            >
                                ✅ Terminar y Cobrar
                            </button>
                        )}

                        {/* Edit Payment Button */}
                        {canEditPayment && (
                            <button
                                className="btn"
                                onClick={() => {
                                    setPaymentForm(prev => ({
                                        ...prev,
                                        appointmentId: appointment.id,
                                        amount: appointment.paymentAmount || appointment.servicePrice || pricePerService,
                                        paymentMethod: (appointment.paymentMethod as any) || 'CASH'
                                    }));
                                    setCurrentView('complete');
                                }}
                                disabled={submitting}
                                style={{
                                    backgroundColor: '#3b82f6',
                                    color: 'white',
                                    padding: '1rem',
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem'
                                }}
                            >
                                ✏️ Editar Pago
                            </button>
                        )}

                        {/* Delegate Button */}
                        {canDelegate && (
                            <button
                                className="btn"
                                onClick={() => {
                                    setCurrentView('delegate');
                                    loadAvailableBarbers();
                                }}
                                disabled={submitting}
                                style={{
                                    padding: '1rem',
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                    backgroundColor: '#0284c7', // light blue
                                    color: 'white',
                                    border: 'none'
                                }}
                            >
                                🤝 Delegar a Compañero
                            </button>
                        )}

                        {/* Reschedule Button */}
                        {canReschedule && (
                            <button
                                className="btn"
                                onClick={() => setCurrentView('reschedule')}
                                disabled={submitting}
                                style={{
                                    padding: '1rem',
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                    background: 'transparent',
                                    border: '1px solid #f59e0b50',
                                    color: '#f59e0b'
                                }}
                            >
                                📅 Reprogramar
                            </button>
                        )}

                        {/* Cancel Button */}
                        {canCancel && (
                            <button
                                className="btn"
                                onClick={() => setCurrentView('cancel')}
                                disabled={submitting}
                                style={{
                                    backgroundColor: '#ef4444',
                                    color: 'white',
                                    padding: '1rem',
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem'
                                }}
                            >
                                ❌ Cancelar Turno
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Complete View - Payment Form */}
            {currentView === 'complete' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <button
                        className="btn btn-sm"
                        onClick={() => setCurrentView('main')}
                        style={{ alignSelf: 'flex-start', background: '#1a1a1a', border: '1px solid #333', color: '#a3a3a3', borderRadius: '0.5rem' }}
                    >
                        ← Volver
                    </button>

                    <div>
                        <label className="form-label" style={{ color: '#a3a3a3' }}>Cliente</label>
                        <input type="text" className="form-input" style={{ background: '#1a1a1a', border: '1px solid #333', color: '#737373' }} value={appointment.clientName} disabled />
                    </div>
                    <div>
                        <label className="form-label" style={{ color: '#a3a3a3' }}>Monto</label>
                        <input
                            type="number"
                            className="form-input"
                            style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                            value={paymentForm.amount}
                            onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) })}
                        />
                    </div>
                    <div>
                        <label className="form-label" style={{ color: '#a3a3a3' }}>Método de Pago</label>
                        <select
                            className="form-input"
                            style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                            value={paymentForm.paymentMethod}
                            onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value as PaymentRequest['paymentMethod'] })}
                        >
                            <option value="CASH">💵 Efectivo</option>
                            <option value="BANK_TRANSFER">🏦 Transferencia</option>
                            <option value="MERCADO_PAGO">📱 Mercado Pago</option>
                            <option value="DEBIT_CARD">💳 Débito</option>
                            <option value="CREDIT_CARD">💳 Crédito</option>
                        </select>
                    </div>
                    {paymentForm.paymentMethod !== 'CASH' && (
                        <div>
                            <label className="form-label" style={{ color: '#a3a3a3' }}>Referencia de Transacción</label>
                            <input
                                type="text"
                                className="form-input"
                                style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                                placeholder="Número de operación"
                                value={paymentForm.transactionReference}
                                onChange={(e) => setPaymentForm({ ...paymentForm, transactionReference: e.target.value })}
                            />
                        </div>
                    )}
                    {requiresReceipt(paymentForm.paymentMethod) && (
                        <div>
                            <label className="form-label" style={{ color: '#a3a3a3' }}>📷 Foto del Comprobante *</label>
                            {receiptPreview ? (
                                <div style={{ position: 'relative' }}>
                                    {receiptImage?.type === 'application/pdf' ? (
                                        <div style={{ width: '100%', padding: '2rem', backgroundColor: '#2a2a2a', borderRadius: '0.5rem', marginBottom: '0.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px solid #404040' }}>
                                            <span style={{ fontSize: '3rem' }}>📄</span>
                                            <span style={{ color: '#e5e5e5', marginTop: '0.5rem', fontSize: '0.875rem', wordBreak: 'break-all', textAlign: 'center' }}>{receiptImage.name}</span>
                                        </div>
                                    ) : (
                                        <img src={receiptPreview} alt="Comprobante" style={{ width: '100%', borderRadius: '0.5rem', marginBottom: '0.5rem' }} />
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline"
                                        onClick={() => { setReceiptImage(null); setReceiptPreview(null); }}
                                    >
                                        ✕ Quitar
                                    </button>
                                </div>
                            ) : (
                                <div>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <input
                                            type="file"
                                            id="receipt-image-action-camera"
                                            accept="image/*"
                                            capture="environment"
                                            onChange={handleImageCapture}
                                            style={{ display: 'none' }}
                                        />
                                        <label
                                            htmlFor="receipt-image-action-camera"
                                            className="btn btn-outline"
                                            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', cursor: 'pointer', padding: '0.75rem 0.25rem' }}
                                        >
                                            <span style={{ fontSize: '1.25rem' }}>📸</span>
                                            <span style={{ fontSize: '0.75rem', textAlign: 'center' }}>Tomar Foto</span>
                                        </label>

                                        <input
                                            type="file"
                                            id="receipt-image-action-gallery"
                                            accept="image/*,application/pdf"
                                            onChange={handleImageCapture}
                                            style={{ display: 'none' }}
                                        />
                                        <label
                                            htmlFor="receipt-image-action-gallery"
                                            className="btn btn-outline"
                                            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', cursor: 'pointer', padding: '0.75rem 0.25rem' }}
                                        >
                                            <span style={{ fontSize: '1.25rem' }}>🖼️</span>
                                            <span style={{ fontSize: '0.75rem', textAlign: 'center' }}>Galería/Archivo</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                        {appointment.status !== 'COMPLETED' && (
                            <button
                                className="btn"
                                onClick={handleCompletePending}
                                disabled={submitting}
                                style={{ flex: 1, background: 'transparent', border: '1px solid #404040', color: '#a3a3a3', borderRadius: '0.75rem' }}
                            >
                                ⏳ Dejar Pendiente
                            </button>
                        )}
                        <button
                            className="btn"
                            onClick={handleCompleteWithPayment}
                            disabled={submitting}
                            style={{ flex: 1, background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: 'white', border: 'none', borderRadius: '0.75rem', fontWeight: '700', boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)' }}
                        >
                            {submitting ? 'Procesando...' : (appointment.status === 'COMPLETED' ? '✓ Guardar Cambios' : '✓ Cobrar')}
                        </button>
                    </div>
                </div>
            )}

            {/* Cancel View */}
            {currentView === 'cancel' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <button
                        className="btn btn-sm"
                        onClick={() => setCurrentView('main')}
                        style={{ alignSelf: 'flex-start', background: '#1a1a1a', border: '1px solid #333', color: '#a3a3a3', borderRadius: '0.5rem' }}
                    >
                        ← Volver
                    </button>

                    <div style={{
                        backgroundColor: '#ef444415',
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        border: '1px solid #ef444430'
                    }}>
                        <p style={{ margin: 0, color: '#ef4444', fontSize: '0.875rem' }}>
                            ⚠️ El cliente recibirá una notificación de la cancelación con el motivo que indiques.
                        </p>
                    </div>

                    <div>
                        <label className="form-label" style={{ color: '#a3a3a3' }}>Motivo de Cancelación *</label>
                        <textarea
                            className="form-input"
                            placeholder="Ej: Surgió un imprevisto personal, me encuentro enfermo, etc."
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            rows={3}
                            style={{ resize: 'vertical', background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                        <button
                            className="btn"
                            onClick={() => setCurrentView('main')}
                            disabled={submitting}
                            style={{ flex: 1, background: 'transparent', border: '1px solid #404040', color: '#a3a3a3', borderRadius: '0.75rem' }}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn"
                            onClick={handleCancelAppointment}
                            disabled={submitting || !cancelReason.trim()}
                            style={{ flex: 1, backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '0.75rem', fontWeight: '700' }}
                        >
                            {submitting ? 'Procesando...' : 'Confirmar Cancelación'}
                        </button>
                    </div>
                </div>
            )}

            {/* Reschedule View */}
            {currentView === 'reschedule' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <button
                        className="btn btn-sm"
                        onClick={() => setCurrentView('main')}
                        style={{ alignSelf: 'flex-start', background: '#1a1a1a', border: '1px solid #333', color: '#a3a3a3', borderRadius: '0.5rem' }}
                    >
                        ← Volver
                    </button>

                    <div style={{
                        backgroundColor: '#f59e0b15',
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        border: '1px solid #f59e0b30'
                    }}>
                        <p style={{ margin: 0, color: '#f59e0b', fontSize: '0.875rem' }}>
                            📅 Podés seleccionar cualquier horario, incluso fuera de tu horario habitual como compensación.
                        </p>
                    </div>

                    <div>
                        <label className="form-label" style={{ color: '#a3a3a3' }}>Nueva Fecha *</label>
                        <input
                            type="date"
                            className="form-input"
                            value={rescheduleDate}
                            onChange={(e) => {
                                setRescheduleDate(e.target.value);
                                setRescheduleTime(''); // Reset time when date changes
                            }}
                            min={getLocalDateString()}
                            style={{
                                border: '1px solid #f59e0b50',
                                backgroundColor: '#1a1a1a',
                                color: '#e5e5e5'
                            }}
                        />
                    </div>

                    {rescheduleDate && (
                        <div>
                            <label className="form-label" style={{ color: '#a3a3a3' }}>Horarios Disponibles *</label>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: '0.5rem',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                padding: '0.5rem',
                                backgroundColor: '#1a1a1a',
                                borderRadius: '0.5rem',
                                border: '1px solid #2a2a2a'
                            }}>
                                {/* Generate time slots using fixed 45-min blocks */}
                                {[
                                    '09:15', '10:00', '10:45', '11:30', '12:15', '13:00',
                                    '13:45', '14:30', '15:15', '16:00', '16:45', '17:30',
                                    '18:15', '19:00', '19:45', '20:30', '21:15', '22:00'
                                ].map(timeStr => {
                                    const isSelected = rescheduleTime === timeStr;

                                    return (
                                        <button
                                            key={timeStr}
                                            type="button"
                                            onClick={() => setRescheduleTime(timeStr)}
                                            style={{
                                                padding: '0.75rem 0.5rem',
                                                backgroundColor: isSelected ? '#f59e0b' : '#111111',
                                                color: isSelected ? '#000' : '#a3a3a3',
                                                border: isSelected ? '2px solid #f59e0b' : '1px solid #333',
                                                borderRadius: '0.5rem',
                                                cursor: 'pointer',
                                                fontWeight: isSelected ? '700' : '500',
                                                fontSize: '0.875rem',
                                                transition: 'all 0.2s',
                                                boxShadow: isSelected ? '0 2px 8px rgba(245, 158, 11, 0.3)' : 'none'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isSelected) {
                                                    e.currentTarget.style.backgroundColor = '#2a2a2a';
                                                    e.currentTarget.style.borderColor = '#f59e0b50';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isSelected) {
                                                    e.currentTarget.style.backgroundColor = '#111111';
                                                    e.currentTarget.style.borderColor = '#333';
                                                }
                                            }}
                                        >
                                            {timeStr}
                                        </button>
                                    );
                                })}
                            </div>
                            {rescheduleTime && (
                                <p style={{
                                    marginTop: '0.75rem',
                                    color: '#f59e0b',
                                    fontWeight: '600',
                                    fontSize: '0.875rem'
                                }}>
                                    ✓ Horario seleccionado: {rescheduleTime}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Reason field - shown after selecting date and time */}
                    {rescheduleDate && rescheduleTime && (
                        <div>
                            <label className="form-label" style={{ color: '#a3a3a3' }}>📝 Motivo de Reprogramación *</label>
                            <textarea
                                className="form-input"
                                placeholder="Ej: Surgió un imprevisto, cambio de horario en la sucursal, etc."
                                value={rescheduleReason}
                                onChange={(e) => setRescheduleReason(e.target.value)}
                                rows={3}
                                style={{ resize: 'vertical', background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                            />
                            <p style={{ fontSize: '0.75rem', color: '#525252', marginTop: '0.25rem' }}>
                                Este motivo se enviará al cliente por email.
                            </p>
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                        <button
                            className="btn"
                            onClick={() => setCurrentView('main')}
                            disabled={submitting}
                            style={{ flex: 1, background: 'transparent', border: '1px solid #404040', color: '#a3a3a3', borderRadius: '0.75rem' }}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn"
                            onClick={handleRescheduleAppointment}
                            disabled={submitting || !rescheduleDate || !rescheduleTime || !rescheduleReason.trim()}
                            style={{ flex: 1, background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#000', border: 'none', borderRadius: '0.75rem', fontWeight: '700', boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)' }}
                        >
                            {submitting ? 'Procesando...' : 'Confirmar Nueva Fecha'}
                        </button>
                    </div>
                </div>
            )}

            {/* Delegate View */}
            {currentView === 'delegate' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <button
                        className="btn btn-sm"
                        onClick={() => setCurrentView('main')}
                        style={{ alignSelf: 'flex-start', background: '#1a1a1a', border: '1px solid #333', color: '#a3a3a3', borderRadius: '0.5rem' }}
                    >
                        ← Volver
                    </button>

                    <div style={{
                        backgroundColor: '#0ea5e915',
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        border: '1px solid #0ea5e930'
                    }}>
                        <p style={{ margin: 0, color: '#0ea5e9', fontSize: '0.875rem' }}>
                            ℹ️ Se listan únicamente los compañeros que son parte del staff y que están libres en este exacto horario.
                            Tú seguirás siendo el dueño del "paquete" (para cobro), pero él atenderá al cliente.
                        </p>
                    </div>

                    <div>
                        <label className="form-label" style={{ color: '#a3a3a3' }}>Compañero disponible *</label>
                        {loadingBarbers ? (
                            <div style={{ padding: '1rem', textAlign: 'center', color: '#a3a3a3' }}>Buscando compañeros...</div>
                        ) : availableBarbers.length === 0 ? (
                            <div style={{ padding: '1rem', textAlign: 'center', color: '#ef4444', backgroundColor: '#ef444415', borderRadius: '0.5rem', border: '1px solid #ef444430' }}>
                                No hay compañeros libres en este horario o con el servicio habilitado.
                            </div>
                        ) : (
                            <select
                                className="form-input"
                                value={selectedBarberId}
                                onChange={(e) => setSelectedBarberId(Number(e.target.value))}
                                style={{ background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5' }}
                            >
                                <option value="" disabled>Seleccione un compañero...</option>
                                {availableBarbers.map(barber => (
                                    <option key={barber.id} value={barber.id}>
                                        {barber.name} {barber.nickname ? `(${barber.nickname})` : ''}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                        <button
                            className="btn"
                            onClick={() => setCurrentView('main')}
                            disabled={submitting}
                            style={{ flex: 1, background: 'transparent', border: '1px solid #404040', color: '#a3a3a3', borderRadius: '0.75rem' }}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn"
                            onClick={handleDelegateAppointment}
                            disabled={submitting || !selectedBarberId}
                            style={{ flex: 1, background: 'linear-gradient(135deg, #0284c7, #0369a1)', color: 'white', border: 'none', borderRadius: '0.75rem', fontWeight: '700', boxShadow: '0 4px 15px rgba(2, 132, 199, 0.3)' }}
                        >
                            {submitting ? 'Delegando...' : 'Confirmar Delegación'}
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default AppointmentActionModal;
